import { Box, Button, Dialog, DialogActions, DialogContent, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import CopyToClipboardButton from "server/components/CopyToClipboardButton";
import { FailedGoogleUsersTable, GoogleUsersTable } from "./GoogleUsersTable";
import ModalTitle, {
  withModalSx,
  modalCancelButtonSx,
  modalConfirmButtonSx,
} from "server/components/Modals/ModalTitle";

// Emails are joined with ";" so the list can be pasted straight into the "To" field of a mail client.
const EMAIL_SEPARATOR = ";";

const GoogleUsersDialog = ({
  open,
  onClose,
  title,
  created = [],
  failed = [],
  // Batching props - supplied only by the generate flow, so the read-back list stays a plain list.
  // `generating` only disables the footer here; the spinner is owned by GenerateGoogleUsersButton, which also
  // covers the very first batch (run from ConfirmModal, before this dialog exists).
  remainingCount = 0,
  skippedCount = 0,
  batchSize = 0,
  onGenerateNext,
  generating = false,
}) => {
  const canGenerateNext = Boolean(onGenerateNext) && remainingCount > 0;
  const nextBatchCount = Math.min(batchSize || remainingCount, remainingCount);

  const emails = created
    .map(row => row.googleEmail)
    .filter(Boolean)
    .join(EMAIL_SEPARATOR);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      sx={withModalSx()}
    >
      <ModalTitle
        icon={<GroupAddIcon />}
        title={title}
      />
      <DialogContent dividers>
        {created.length === 0 && failed.length === 0 ? (
          <Typography sx={{ p: 2 }}>Няма генерирани Google акаунти за тази кампания.</Typography>
        ) : null}

        {created.length > 0 ? (
          <>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "0.5rem",
                gap: 2,
              }}
            >
              <Typography fontWeight="bold">Google акаунти ({created.length})</Typography>
              <CopyToClipboardButton
                text={emails}
                label="Копирай всички имейли"
                copiedLabel="Имейлите са копирани"
                successMessage={`Копирани ${created.length} имейла, разделени с "${EMAIL_SEPARATOR}"`}
              />
            </Box>
            <GoogleUsersTable rows={created} />
            <Typography
              variant="body2"
              color="text.secondary"
              fontStyle="italic"
              sx={{ marginTop: "0.5rem" }}
            >
              Паролите са първоначални — кандидатът ще бъде задължен да я смени при първото влизане.
            </Typography>
          </>
        ) : null}

        {failed.length > 0 ? <FailedGoogleUsersTable rows={failed} /> : null}

        {canGenerateNext ? (
          <Typography
            color="error"
            fontWeight="bold"
            sx={{ marginTop: "1.5rem" }}
          >
            Остават още {remainingCount} кандидатури без Google акаунт. Създаването на един акаунт отнема няколко
            секунди, затова акаунтите се генерират на партиди.
            {skippedCount > 0 ? ` ${skippedCount} са пропуснати след неуспешен опит и няма да бъдат опитани пак.` : ""}
          </Typography>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ display: "flex", justifyContent: "space-between", px: 3, py: 2 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          color="error"
          size="medium"
          startIcon={<CloseIcon />}
          disabled={generating}
          sx={modalCancelButtonSx}
        >
          Затвори
        </Button>
        {canGenerateNext ? (
          <Button
            onClick={onGenerateNext}
            variant="contained"
            color="primary"
            size="medium"
            startIcon={<GroupAddIcon />}
            disabled={generating}
            sx={modalConfirmButtonSx}
          >
            Генерирай следващите {nextBatchCount}
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  );
};

export default GoogleUsersDialog;
